import { useEffect, useRef, MutableRefObject } from 'react';
import useTabIsActive from 'src/hooks/utils/useTabIsActive';
import usePricingSourceStore from 'src/stores/pricing/usePricingSourceStore';
import {
	PRICING_FALLBACK_COOLDOWN_MS,
	PRICING_RECOVERY_CHECK_INTERVAL_MS,
	PRICING_TAB_INACTIVE_DISCONNECT_DELAY_MS,
} from '../constants';

interface UsePricingTabVisibilityAndRecoveryParams {
	lastFallbackTimestamp: MutableRefObject<number>;
	/**
	 * Callback to perform full recovery. This callback may depend on
	 * destroyCurrentPipeline$ internally, but the tab visibility effect
	 * should NOT directly depend on destroyCurrentPipeline$.
	 */
	performRecovery: () => void;
	/**
	 * Callback for tab inactive cleanup. Called inside the timeout handler.
	 */
	onTabInactiveCleanup: () => void;
}

/**
 * Hook that handles tab visibility changes and periodic recovery checks.
 *
 * Similar to useTabVisibilityAndRecovery for orderbook, this hook:
 * - Monitors tab visibility changes
 * - Disconnects after prolonged inactivity
 * - Reconnects when tab becomes active
 * - Periodically attempts recovery when in polling mode
 */
export function usePricingTabVisibilityAndRecovery({
	lastFallbackTimestamp,
	performRecovery,
	onTabInactiveCleanup,
}: UsePricingTabVisibilityAndRecoveryParams): void {
	const source = usePricingSourceStore((s) => s.source);

	const isTabActive = useTabIsActive();
	const wasTabActiveRef = useRef(isTabActive);
	const inactiveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Tab visibility handling
	useEffect(() => {
		const tabJustBecameActive = isTabActive && !wasTabActiveRef.current;
		const tabJustBecameInactive = !isTabActive && wasTabActiveRef.current;
		wasTabActiveRef.current = isTabActive;

		// Tab became inactive: set timeout to disconnect
		if (tabJustBecameInactive) {
			if (inactiveTimeoutRef.current) clearTimeout(inactiveTimeoutRef.current);

			inactiveTimeoutRef.current = setTimeout(() => {
				onTabInactiveCleanup();
				inactiveTimeoutRef.current = null;
			}, PRICING_TAB_INACTIVE_DISCONNECT_DELAY_MS);
			return;
		}

		// Tab became active: cancel timeout or reconnect
		if (tabJustBecameActive) {
			// If timeout is still pending, just cancel it
			if (inactiveTimeoutRef.current) {
				clearTimeout(inactiveTimeoutRef.current);
				inactiveTimeoutRef.current = null;
				return;
			}

			// Otherwise, we were disconnected - do full reconnect
			performRecovery();
		}
	}, [isTabActive, performRecovery, onTabInactiveCleanup]);

	// Periodic recovery check
	useEffect(() => {
		const checkRecoveryInterval = setInterval(() => {
			if (document.hidden) return; // Skip recovery attempts when tab is inactive
			if (source === 'polling') {
				const timeSinceLastFallback =
					Date.now() - lastFallbackTimestamp.current;
				const cooldownComplete =
					timeSinceLastFallback > PRICING_FALLBACK_COOLDOWN_MS;

				if (cooldownComplete) {
					lastFallbackTimestamp.current = 0;
					performRecovery();
				}
			}
		}, PRICING_RECOVERY_CHECK_INTERVAL_MS);

		return () => clearInterval(checkRecoveryInterval);
	}, [source, performRecovery, lastFallbackTimestamp]);

	// Cleanup inactive timeout on unmount
	useEffect(() => {
		return () => {
			if (inactiveTimeoutRef.current) {
				clearTimeout(inactiveTimeoutRef.current);
				inactiveTimeoutRef.current = null;
			}
		};
	}, []);
}
